import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown as BootstrapDropdown } from 'bootstrap';
import { useThemes } from '@hooks/useThemes';
import styles from './Button.module.css';
import { useClickDetector } from '@hooks/useClickDetector';

export enum ButtonMode {
    primary = 'primary',
    secondary = 'secondary',
    light = 'light',
    danger = 'danger',
    link = 'link',
}

export interface ButtonProps {
    id?: string;
    classes?: string;
    type?: 'button' | 'submit';
    label: string;
    icon?: string;
    iconPosition?: 'left' | 'farLeft';
    mode: ButtonMode;
    size?: 'sm' | 'md';
    outline?: boolean;
    disabled?: boolean;
    textColor?: 'light'; // may be used with mode 'secondary'
    bgColor?: 'blue'; // may be used with mode 'primary'
    tall?: boolean; // may be used with small buttons
    dropdown?: {
        side?: 'left' | 'right';
        options: {
            label: string;
            icon?: string;
            onClick?: () => void;
        }[];
    }; // will be used to toggle a dropdown?
    onClick?: () => void;
}

export const Button: FC<ButtonProps> = ({
    id,
    classes,
    type,
    label,
    icon,
    iconPosition,
    mode,
    size,
    outline,
    disabled,
    textColor,
    bgColor,
    tall,
    dropdown,
    onClick,
}) => {
    const { useGrayscaleTheme } = useThemes();

    const commonClasses =
        // mode + outline
        `btn ${styles.btn} btn-${outline ? 'outline-' : ''}${mode} ${styles[`btn-${outline ? 'outline-' : ''}${mode}`]}` +
        // size
        ` ${size === 'sm' ? `btn-sm ${styles['btn-sm']} ${tall ? styles['btn-sm-tall'] : ''}` : ''}` +
        // override text color
        ` ${textColor === 'light' ? styles['btn-text-light'] : ''}` +
        // override bg color
        ` ${bgColor === 'blue' ? styles['btn-bg-blue'] : ''}` +
        // override icon position
        ` ${iconPosition === 'farLeft' ? styles['far-left-icon'] : ''}` +
        // themes
        ` ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
        // extra classes
        ` ${classes || ''}`;

    // for dropdown
    const [instance, setInstance] = useState<BootstrapDropdown | null>(null);
    const [show, setShow] = useState<boolean>(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // destroy the dropdown when the component unmounts
        return () => {
            try {
                instance?.dispose();
            } catch (e) {
                console.log('catch error disposing of dropdown');
            }
        };
    }, [instance]);

    const onRefChange = useCallback((node: HTMLUListElement) => {
        if (node !== null) {
            const newInstance = BootstrapDropdown.getOrCreateInstance(node, { display: 'static' });
            setInstance(newInstance);
        }
    }, []);

    useEffect(() => {
        show ? instance?.show() : instance?.hide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useClickDetector([{ elemRef: dropdownRef, onClickOutside: () => setShow(false) }]);

    return (
        <>
            {dropdown ? (
                <div className="position-relative" ref={dropdownRef}>
                    <button
                        id={id || undefined}
                        type="button"
                        className={`${commonClasses} dropdown-toggle ${styles['dropdown-toggle']}`}
                        disabled={disabled}
                        data-testid="button"
                        onClick={() => setShow(!show)}>
                        {label}
                        <span className={label ? styles.toggler : styles['arrow-only-toggler']}>
                            <i className="fa-light fa-chevron-down" />
                        </span>
                    </button>
                    <ul
                        ref={onRefChange}
                        className={`dropdown-menu ${styles['dropdown-menu']} ${dropdown.side === 'right' ? styles['dropdown-menu-end'] : ''} ${show ? 'show' : ''}`}>
                        {dropdown.options.map((item, index) => (
                            <li key={index}>
                                <button
                                    type="button"
                                    className={`dropdown-item ${styles['dropdown-item']}`}
                                    onClick={() => {
                                        item.onClick && item.onClick();
                                        setShow(false);
                                    }}>
                                    {item.icon && <i className={item.icon} />} {item.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <button
                    id={id || undefined}
                    type={type || 'button'}
                    className={commonClasses}
                    disabled={disabled}
                    data-testid="button"
                    onClick={() => onClick && onClick()}>
                    {icon && <i className={icon} />} {label}
                </button>
            )}
        </>
    );
};
